import { EnumGoodsDetailIsSpike } from '../defines/enums'
import { GlobalEnv } from '../global_env'
import { URLQueries, getUrlBizExtraParams } from '../global_env/url_query'
import { proxyCamelCase, timestamp2hms } from './format'
import { request } from '../api/request'
import { isDyProgramH5 } from '@/lib/js/env'
import {
  bridge,
  is,
  build,
  goTo,
  query as webxQuery,
  isHK,
  xenv,
} from '@didi/webx-js'
import { HKDomainConvert } from '../../js/hk_url'
import {
  clickGoodsGuideMpAppHome,
  getMpAppHomeXenvInfo,
} from './guide_app_home'
import { mallTrack } from '../common_methods'
import { getTrackUrl, transformUrl } from '@/lib/js/util'

/**
 * 设置页面标题
 * @param {string} title - 页面标题
 */
export const setTitle = title => {
  if (__mpx_mode__ === 'web' && ['passenger', 'hypassenger'].includes(xenv())) {
    bridge.setTitle({
      /* cspell: disable-next-line */
      navi_title: title,
    })
  } else {
    document.title = title
  }
}

/**
 * 内部页面跳转
 * @param {string} pathname - 页面路径
 * @param {Object} query - URL Query 参数
 */
export const navigateTo = (pathname, query = {}, options = {}) => {
  if (!pathname) return
  // 抖音下跳转首页
  const adParams = getUrlBizExtraParams()
  if (isDyProgramH5() && (pathname === 'index' || pathname === 'goIndex')) {
    goTo({
      path: '/pages/home/index',
      params: adParams,
      extParams: {
        moveType: 2, // 0（navigateTo）、1（redirectTo）、2（reLaunch）
      },
    })
    return
  }
  const queries = {
    ...webxQuery(),
    lang: GlobalEnv.lang,
    openid: GlobalEnv.openid,
    from: GlobalEnv.from,
    dchn: URLQueries.getQuery('dchn'),
    ...query,
  }
  pathname === 'goIndex' && delete queries.id
  if (URLQueries.getQuery('from') === 'marking' || xenv() === 'yxwxmp') {
    Object.assign(queries, {
      from: 'marking',
      token: GlobalEnv.loginInfo.token,
    })
  }
  if (isDyProgramH5()) {
    Object.assign(queries, adParams)
  }
  const url = transformUrl(pathname)
  setTimeout(() => {
    goTo({
      url,
      params: queries,
      extParams: {
        h5JumpMethod: 'href',
        sameProduct: true,
        ...(options?.extParams || {}),
      },
    })
  }, 0)
}
/**
 * 页面重定向
 * @param {string} pathname - 页面路径
 * @param {Object} query - URL Query 参数
 */
export const redirectTo = (pathname, query) => {
  if (!pathname) return
  const queries = {
    ...webxQuery(),
    lang: GlobalEnv.lang,
    openid: GlobalEnv.openid,
    from: GlobalEnv.from,
    dchn: URLQueries.getQuery('dchn'),
    ...query,
  }
  pathname === 'index' && delete queries.id
  if (URLQueries.getQuery('from') === 'marking' || xenv() === 'yxwxmp') {
    Object.assign(queries, {
      from: 'marking',
      token: GlobalEnv.loginInfo.token,
    })
  }
  setTimeout(() => {
    const url = build(transformUrl(pathname), queries)
    window.location.replace(url)
  }, 0)
}

/**
 * 包装异步函数，一次执行完之前，不能重复执行
 * @param {function} fn
 * @return {function}
 */
export const lockFn = fn => {
  let lock = false
  return async function () {
    if (!lock) {
      try {
        lock = true
        const res = await fn.apply(this, arguments)
        lock = false
        return res
      } catch (error) {
        lock = false
        return Promise.reject(error)
      }
    }
  }
}

/**
 * 根据时间戳生成倒计时的时分秒字符串
 * @param {number} start - 开始时间戳
 * @param {number} end - 结束时间戳
 * @return {function} cancel 函数，可以取消计时器
 */
export const countDown = (start, end, cb) => {
  let state = {
    offset: end - start,
    timer: null,
  }
  let fn = null
  const cancel = () => {
    state.timer && clearTimeout(state.timer)
    state = null
    fn = null
  }
  fn = () => {
    if (state.offset <= 0) {
      cb(timestamp2hms(0))
      cancel()
    } else {
      cb(timestamp2hms(state.offset))
      state.timer = setTimeout(() => {
        state.offset -= 1000
        fn()
      }, 1000)
    }
  }
  fn()
  return cancel
}

const joinEventParams = eventParams =>
  eventParams
    ? Object.keys(eventParams)
        .map(key => `${key}:${eventParams[key]}`)
        .join('|')
    : ''

const _eventUrl = HKDomainConvert(
  'https://winter-campaign.xiaojukeji.com/strategy-genrec/biztech_arch/strategygenrec/event',
  isHK
)

/**
 * 上报智能算法点击事件
 * @param {object} eventParams
 * @param {string} eventUrl
 * @return {function}
 */
const sendRecOrderRequest = (eventParams, eventUrl) => {
  if (GlobalEnv.loginInfo.token) {
    eventUrl = _eventUrl
    request
      .get(eventUrl, eventParams)
      .then(() => {})
      .catch(() => {})
  }
}

export const goGoodsDetail = (_goods, opts, event) => {
  let goods = _goods || {}
  opts = opts || {}
  const {
    channelId,
    locationInfo: { cityId, lat, lng },
    lang,
    clientType,
    openid,
    loginInfo: { token },
  } = GlobalEnv
  switch (opts.source) {
    case 'like': // 猜你喜欢
      goods = proxyCamelCase(_goods)
      mallTrack(
        /* cspell: disable-next-line */
        'c_trippackage_mall_detail_collect_ck',
        '统计周期内具体套餐详情页其他产品点击量',
        {
          pid: GlobalEnv.loginInfo.uid,
          /* cspell: disable-next-line */
          taocan_id: URLQueries.getQuery('id'),
          position: opts.position,
          /* cspell: disable-next-line */
          collect_taocan_id:
            goods.goodsId === undefined
              ? goods.goodsInfo.goodsId
              : goods.goodsId,
        }
      )
      break
    /* cspell: disable-next-line */
    case 'landpage': // 首页【目前没有首页在使用】
      /* cspell: disable-next-line */
      goods = proxyCamelCase(_goods)
      mallTrack(
        'c_trippackage_mall_landpage_goods_ck',
        '落地页中所有商品点击',
        {
          pid: GlobalEnv.loginInfo.uid,
          taocan_id:
            goods.goodsId === undefined
              ? goods.goodsInfo.goodsId
              : goods.goodsId,
          workflow_id:
            event && event.event_info && event.event_info.workflow_id
              ? event.event_info.workflow_id
              : -1,
          uid: GlobalEnv.loginInfo.uid,
          url: getTrackUrl(),
          is_free: goods?.creditInfo?.status,
          product_type: goods.productType,
          city_id: GlobalEnv.locationInfo?.cityId,
          tab_id: goods.cateId,
          is_dim_p: goods.isDimP,
        }
      )
      break
  }
  if (goods.goodsType === 3) {
    // 畅拼卡埋点
    /* cspell: disable-next-line */
    mallTrack('wyc_mall_pinchecard_ck', '落地页中所有商品点击', {
      pg_type: 3,
      type: goods.priority,
      fr: xenv(),
      uid: GlobalEnv.loginInfo.uid,
      ...(goods?.omegaInfo || {}),
    })
  }
  if (event) {
    const eventParams = Object.assign({}, event.event_info, {
      item_id:
        goods.goodsId === undefined ? goods.goodsInfo.goodsId : goods.goodsId,
      expose_page: goods.page,
      expose_pos: goods.index,
      event_type: 'ck',
      event_time: Date.now(),
    })
    // 上报智能算法点击事件
    sendRecOrderRequest(eventParams, event.event_url)
  }
  let packageType = ''
  if (goods.goodsType === 3) {
    // 拼车卡
    if (goods.goodsName.indexOf('7天') > -1) {
      packageType = 7
    } else if (goods.goodsName.indexOf('30天') > -1) {
      packageType = 30
    }
  }
  mallTrack('coupon_package_ck', '从套餐列表点击跳转详情', {
    pid: GlobalEnv.loginInfo.uid,
    channelId: URLQueries.getQuery('dchn') || URLQueries.getQuery('channelId'),
    /* cspell: disable-next-line */
    iterm_type: goods.goodsType === 3 ? 4 : 0,
    card_type: packageType,
    price: goods.price,
  })

  const params = {
    //  URL 参数算吗？待确认？
    channelId,
    lat,
    lng,
    lang,
    trip_cityid: URLQueries.getQuery('trip_cityid'),
    client_type: clientType,
    cityId,
    access_key_id: URLQueries.getQuery('access_key_id'),
    openid: openid || '',
    dchn: URLQueries.getQuery('dchn') || '',
    uid: GlobalEnv.loginInfo.uid,
    token,
    source_disable_dchn: URLQueries.getQuery('source_disable_dchn'),
    show_version: goods.showVersion,
    live_status: URLQueries.getQuery('live_status'), // 抖音直播状态
  }
  const xenvInfo = getMpAppHomeXenvInfo({
    product_Type: goods.productType,
    link_url: goods.linkUrl,
    credit_info: goods.creditInfo,
  })
  if (xenvInfo && xenvInfo.otherDetailPageJumpAppHome) {
    clickGoodsGuideMpAppHome(xenvInfo, params)
    return
  }
  let url
  if (goods.linkUrl) {
    Object.assign(params, {
      from: URLQueries.getQuery('from'),
      ts: Date.now(),
      ...(__mpx_mode__ === 'web' && is('miniprogram')
        ? /* cspell: disable-next-line */
          { source: 'mpstore' }
        : {}),
    })
    url = goods.linkUrl
  } else {
    Object.assign(params, {
      id: goods.goodsId === undefined ? goods.goodsInfo.goodsId : goods.goodsId,
      prev: opts.source || 0,
      from: GlobalEnv.from,
    })
    let pathname
    switch (goods.isSpike) {
      case EnumGoodsDetailIsSpike.LimitTime:
      case EnumGoodsDetailIsSpike.LimitCount:
        Object.assign(params, {
          // TODO: 这玩意儿要提到配置里合适一些？
          pid: 10002, // 商城业务 id
        })
        pathname = 'goodsDetail'
        break
      default:
        pathname = `goodsDetail`
        break
    }
    event &&
      Object.assign(params, {
        eventParams: joinEventParams(event.event_info),
      })
    url = transformUrl(pathname)
  }
  goTo({
    url,
    params,
    extParams: {
      h5JumpMethod: 'href',
      sameProduct: url.indexOf('quartz-mall') > -1 || url.indexOf('./') === 0,
    },
  })
}

/** 商品使用限制  */
export function getGoodsTip(goods, needCity = true) {
  if (isDyProgramH5()) {
    const explains = ['不支持7天无理由退货']
    if (needCity && goods.city_name) {
      explains.unshift(`适用于${goods.city_name}`)
    }
    return explains
  }
  return null
}

/** 跳到规则页面 */
export const toLaw = lang => {
  console.log('toLaw', lang)
  const RULE_link = {
    default: {
      'zh-CN': 'https://s.didi.cn/ryloXb',
      'en-US': 'https://v.didi.cn/p/7KMM7M',
      'zh-HK': 'https://v.didi.cn/xAbj9p7',
      'ko-KR': 'https://v.didi.cn/3WXKPoP',
    },
    dymp: {
      'zh-CN':
        'https://dpubstatic.udache.com/static/dpubimg/be7b36f359e67d9d18a28aa437984429/index.html',
      'en-US': '',
      'zh-HK': '',
    },
  }
  const rules = RULE_link[xenv()] || RULE_link.default
  goTo({ url: rules[lang || GlobalEnv.lang] || rules['zh-CN'] })
}
